import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { APP_LANGUAGE_CODES, DEFAULT_LANGUAGE } from '@/localization/languages';

const localeModuleLoaders = import.meta.glob<{ default: Record<string, unknown> }>('../locales/*/portal.json');

function normalizeLanguageCode(language: string): string {
  if (!language) return DEFAULT_LANGUAGE;
  const shortCode = language.toLowerCase().split(/[-_]/)[0];
  return shortCode === 'uk' ? 'ua' : shortCode;
}

function getLocaleLoader(language: string, namespace: string) {
  const normalizedLanguage = normalizeLanguageCode(language);
  const modulePath = `../locales/${normalizedLanguage}/${namespace}.json`;
  return localeModuleLoaders[modulePath] ?? null;
}

i18n
  .use(LanguageDetector)
  .use(
    resourcesToBackend((language, namespace) => {
      const loader = getLocaleLoader(language, namespace);
      if (loader) {
        return loader().then((moduleValue) => moduleValue.default);
      }

      const fallbackLoader = getLocaleLoader(DEFAULT_LANGUAGE, namespace);
      if (fallbackLoader) {
        return fallbackLoader().then((moduleValue) => moduleValue.default);
      }

      return Promise.resolve({});
    })
  )
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: APP_LANGUAGE_CODES,
    defaultNS: 'portal',
    ns: ['portal'],
    load: 'languageOnly',
    partialBundledLanguages: true,

    detection: {
      // Only detect from cookie/localStorage, don't auto-detect browser language
      order: ['cookie', 'localStorage'],
      caches: [], // Disable automatic caching - let LanguageContext handle this
      lookupCookie: 'language',
      lookupLocalStorage: 'language',
    },

    interpolation: {
      escapeValue: false, // React already does escaping
    },

    react: {
      useSuspense: false,
    },
  });

// Make i18n available globally for syncing
if (typeof window !== 'undefined') {
  (window as any).i18n = i18n;
}

export default i18n;
